import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Heart, Share2, Sparkles, Crown, Check } from "lucide-react";
import { toast } from "sonner";

// Compare 2–4 AI try-ons side by side, vote your favorite, and share the grid.
export default function CompareLooks() {
  const { user } = useAuth();
  const [looks, setLooks] = useState([]);
  const [picked, setPicked] = useState([]); // array of session ids
  const [votes, setVotes] = useState({}); // { sessionId: count } (local + persisted)
  const [voted, setVoted] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.tryOnMine().then(({ data }) => setLooks(data || [])).catch(() => {});
    // hydrate local votes
    try {
      const v = JSON.parse(localStorage.getItem("hb_compare_votes") || "{}");
      setVotes(v);
    } catch {}
  }, [user]);

  const chosen = useMemo(() => picked.map((id) => looks.find((l) => l.id === id)).filter(Boolean), [picked, looks]);

  const toggle = (id) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) {
        toast.error("Pick up to 4 looks");
        return prev;
      }
      return [...prev, id];
    });
  };

  const castVote = (sessionId) => {
    if (voted) return;
    const next = { ...votes, [sessionId]: (votes[sessionId] || 0) + 1 };
    setVotes(next);
    setVoted(sessionId);
    localStorage.setItem("hb_compare_votes", JSON.stringify(next));
    toast.success("You picked your favorite!");
  };

  const shareGrid = async () => {
    if (chosen.length < 2) { toast.error("Pick at least 2 looks to share"); return; }
    setSharing(true);
    try {
      const canvas = document.createElement("canvas");
      const cols = chosen.length;
      const cellW = 512;
      const cellH = 768;
      canvas.width = cellW * cols;
      canvas.height = cellH + 120;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#1a0e0f";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Load all images
      const imgs = await Promise.all(chosen.map((c) => new Promise((res, rej) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = `${process.env.REACT_APP_BACKEND_URL}${c.result_url}`;
      })));
      imgs.forEach((img, i) => {
        // cover crop
        const s = Math.max(cellW / img.width, cellH / img.height);
        const w = img.width * s, h = img.height * s;
        const dx = i * cellW + (cellW - w) / 2;
        const dy = (cellH - h) / 2;
        ctx.drawImage(img, dx, dy, w, h);
        // label
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.fillRect(i * cellW + 12, cellH - 60, 180, 40);
        ctx.fillStyle = "#e6c48f";
        ctx.font = "600 22px 'Cormorant Garamond', serif";
        ctx.fillText(chosen[i].product_name?.slice(0, 20) || `Look ${i + 1}`, i * cellW + 24, cellH - 33);
      });
      // Footer branding
      ctx.fillStyle = "#e6c48f";
      ctx.font = "700 34px 'Cormorant Garamond', serif";
      ctx.fillText("HEATHER'S BOUTIQUE · Pick my prom look ↓", 30, cellH + 60);
      ctx.fillStyle = "#faf6f1";
      ctx.font = "600 20px 'Inter', sans-serif";
      ctx.fillText("heathersboutique.com/compare", 30, cellH + 95);
      // Download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `heathers-compare-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        // Native share if supported
        if (navigator.share && navigator.canShare?.({ files: [new File([blob], "compare.png", { type: "image/png" })] })) {
          navigator.share({
            title: "Which prom look is your fave?",
            text: "Help me pick my prom look 💕",
            files: [new File([blob], "compare.png", { type: "image/png" })],
          }).catch(() => {});
        } else {
          toast.success("Saved! Post it to your story and tag @heathersboutique");
        }
      }, "image/png");
    } catch (e) {
      toast.error("Couldn't build your share card — try again.");
    } finally {
      setSharing(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-24 text-center" data-testid="compare-signin">
        <h1 className="font-display text-5xl sm:text-6xl">Compare your looks</h1>
        <p className="font-body text-muted-foreground mt-4 mb-8">Sign in to see your AI try-ons side-by-side.</p>
        <Link to="/login" className="bg-ink text-ivory px-7 py-3.5 font-label text-[10px] rounded-full">SIGN IN</Link>
      </div>
    );
  }

  const crumbs = [{ name: "Home", path: "/" }, { name: "Compare Looks", path: "/compare" }];

  return (
    <div data-testid="compare-page" className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-24">
      <Seo title="Compare My Prom Looks" description="Stack your AI try-ons side-by-side and pick your prom look. Share the grid with your friends to vote." path="/compare" />
      <Breadcrumbs items={crumbs} />
      <p className="font-label text-[10px] text-rosemetal tracking-widest mt-4">SHARE. VOTE. LOCK.</p>
      <h1 className="font-display text-5xl sm:text-6xl mt-2">Which one wins prom?</h1>
      <p className="font-body text-sm text-muted-foreground mt-3 max-w-xl">
        Pick 2–4 looks from your AI try-ons, see them side-by-side, and share the grid to your group chat. First to hit 10 votes locks the win.
      </p>

      {looks.length === 0 ? (
        <div className="mt-12 border border-dashed border-border rounded-sm p-12 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-rosemetal opacity-60" />
          <h2 className="font-display text-2xl mt-4">No try-ons yet</h2>
          <p className="font-body text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Try on 2 or more dresses first, then come back to compare them side-by-side.
          </p>
          <Link to="/prom-dresses" className="inline-block mt-6 bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-full">SHOP PROM DRESSES</Link>
        </div>
      ) : (
        <>
          {/* Selection tray */}
          <div className="mt-8">
            <div className="flex items-baseline justify-between mb-4">
              <p className="font-label text-[10px] text-muted-foreground tracking-widest">
                YOUR TRY-ONS · TAP TO PICK ({picked.length}/4)
              </p>
              <button
                data-testid="compare-share-btn"
                onClick={shareGrid}
                disabled={sharing || chosen.length < 2}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-label text-[10px] tracking-widest transition-colors ${chosen.length >= 2 ? "bg-rosemetal text-ivory hover:bg-ink" : "bg-border text-muted-foreground cursor-not-allowed"}`}
              >
                <Share2 className="w-3 h-3" /> {sharing ? "BUILDING…" : "SHARE THE GRID"}
              </button>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {looks.map((l) => {
                const isPicked = picked.includes(l.id);
                return (
                  <button
                    key={l.id}
                    onClick={() => toggle(l.id)}
                    data-testid={`compare-pick-${l.id}`}
                    className={`relative aspect-[3/4] overflow-hidden rounded-sm border-2 transition-all ${isPicked ? "border-rosemetal ring-2 ring-rosemetal/30" : "border-border hover:border-ink/40"}`}
                  >
                    <img src={`${API}${l.result_url}`} alt={l.product_name} className="w-full h-full object-cover object-top" />
                    {isPicked && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-rosemetal text-ivory flex items-center justify-center">
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent p-2">
                      <div className="font-display text-xs text-ivory leading-tight line-clamp-2 text-left">{l.product_name}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Side-by-side stage */}
          {chosen.length >= 2 && (
            <div className="mt-16" data-testid="compare-stage">
              <p className="font-label text-[10px] text-rosemetal tracking-widest">THE RUNWAY · TAP YOUR FAVE</p>
              <h2 className="font-display text-3xl sm:text-4xl mt-2">Which look is winning?</h2>
              <div className={`mt-8 grid gap-4 ${chosen.length === 2 ? "grid-cols-2" : chosen.length === 3 ? "grid-cols-3" : "grid-cols-2 md:grid-cols-4"}`}>
                {chosen.map((l, i) => (
                  <motion.div
                    key={l.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className="relative group"
                  >
                    <div className={`aspect-[3/4] overflow-hidden rounded-sm relative ${voted === l.id ? "ring-4 ring-champagne shadow-[0_0_40px_rgba(230,196,143,0.5)]" : ""}`}>
                      <img src={`${API}${l.result_url}`} alt={l.product_name} className="w-full h-full object-cover object-top" />
                      {voted === l.id && (
                        <div className="absolute top-3 left-3 bg-champagne text-ink px-3 py-1.5 font-label text-[10px] tracking-widest rounded-full flex items-center gap-1.5">
                          <Crown className="w-3 h-3" /> YOUR PICK
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display text-lg leading-tight">{l.product_name}</div>
                        <div className="font-label text-[9px] text-muted-foreground mt-0.5 tracking-widest">
                          {l.product_color}{l.product_price ? ` · $${l.product_price}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => castVote(l.id)}
                        disabled={!!voted}
                        data-testid={`compare-vote-${l.id}`}
                        className={`shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full font-label text-[9px] tracking-widest transition-colors ${voted === l.id ? "bg-champagne text-ink" : voted ? "bg-border text-muted-foreground" : "bg-ink text-ivory hover:bg-rosemetal"}`}
                      >
                        <Heart className={`w-3 h-3 ${voted === l.id ? "fill-current" : ""}`} strokeWidth={2} />
                        {votes[l.id] || 0}
                      </button>
                    </div>
                    {l.category_slug && l.product_slug && (
                      <Link to={`/${l.category_slug}/${l.product_slug}`} className="mt-2 inline-block font-label text-[9px] text-rosemetal tracking-widest hover:underline">
                        LOCK THIS LOOK →
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
